// ============================================
// MoltiVerse v3 - Search Omnibar (Cmd+K)
// ============================================

import { useEffect, useRef, useCallback } from 'react';
import { useUniverseStore } from '@/stores/universe-store';
import { SUBMOLT_COLORS, type Agent } from '@/lib/types';
import { playSound } from '@/lib/use-sound';
import { formatDistanceToNow } from 'date-fns';

export function SearchOmnibar() {
  const inputRef = useRef<HTMLInputElement>(null);
  
  const {
    search,
    closeSearch,
    setSearchQuery,
    setSearchMode,
    focusAgent,
    addToSearchHistory,
    graphData,
  } = useUniverseStore();

  // Auto-focus input and play sound
  useEffect(() => {
    inputRef.current?.focus();
    playSound('search-open');
  }, []);

  const handleSelect = useCallback((agent: Agent) => {
    playSound('search-result');
    addToSearchHistory(agent);
    focusAgent(agent.id);
    closeSearch();
  }, [addToSearchHistory, focusAgent, closeSearch]);
  
  const handleHistorySelect = useCallback((historyItem: { id: string; name: string }) => {
    // Find the full agent from graph data
    const agent = graphData.nodes.find(n => n.id === historyItem.id);
    if (agent) {
      playSound('search-result');
      addToSearchHistory(agent);
      focusAgent(agent.id);
    }
    closeSearch();
  }, [graphData.nodes, addToSearchHistory, focusAgent, closeSearch]);

  const handleClose = useCallback(() => {
    playSound('search-close');
    closeSearch();
  }, [closeSearch]);

  const handlePostSelect = useCallback((post: { author_id: string; author_name: string }) => {
    // Navigate to the post's author
    const agent = graphData.nodes.find(n => n.id === post.author_id);
    if (agent) {
      playSound('search-result');
      addToSearchHistory(agent);
      focusAgent(agent.id);
    }
    closeSearch();
  }, [graphData.nodes, addToSearchHistory, focusAgent, closeSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    } else if (e.key === 'Enter') {
      if (search.mode === 'agents' && search.results.length > 0) {
        handleSelect(search.results[0]);
      } else if (search.mode === 'posts' && search.postResults.length > 0) {
        handlePostSelect(search.postResults[0]);
      } else if (search.query.length === 0 && search.history.length > 0) {
        handleHistorySelect(search.history[0]);
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Toggle between agents and posts mode
      setSearchMode(search.mode === 'agents' ? 'posts' : 'agents');
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] sm:pt-[15vh]"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      
      {/* Search Panel */}
      <div 
        className="relative w-full max-w-2xl mx-2 sm:mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="relative">
          <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-cyber-glow/60">
            <svg className="w-4 sm:w-5 h-4 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={search.query}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={search.mode === 'agents' ? "Search agents..." : "Search posts..."}
            className="w-full bg-cyber-panel border-2 border-cyber-glow/50 rounded-lg pl-10 sm:pl-12 pr-12 sm:pr-4 py-3 sm:py-4 text-base sm:text-lg font-mono text-white placeholder-white/30 outline-none focus:border-cyber-glow transition-colors"
          />
          <div className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <kbd className="hidden sm:block px-2 py-1 bg-white/10 rounded text-xs text-white/40">ESC</kbd>
            <button onClick={handleClose} className="sm:hidden p-1 text-white/40">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mode Tabs */}
        <div className="mt-2 flex gap-1 bg-cyber-panel border border-cyber-border rounded-lg p-1">
          <button
            onClick={() => setSearchMode('agents')}
            className={`flex-1 px-4 py-2 rounded-md font-mono text-sm transition-all ${
              search.mode === 'agents' 
                ? 'bg-cyber-glow/20 text-cyber-glow border border-cyber-glow/30' 
                : 'text-white/50 hover:text-white/80 hover:bg-white/5'
            }`}
          >
            👤 Agents
          </button>
          <button
            onClick={() => setSearchMode('posts')}
            className={`flex-1 px-4 py-2 rounded-md font-mono text-sm transition-all ${
              search.mode === 'posts' 
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                : 'text-white/50 hover:text-white/80 hover:bg-white/5'
            }`}
          >
            📝 Posts
          </button>
          <div className="hidden sm:flex items-center px-2 text-xs text-white/30">
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded">Tab</kbd>
          </div>
        </div>

        {/* Search History - shown when query is empty in agents mode */}
        {search.query.length === 0 && search.mode === 'agents' && search.history.length > 0 && (
          <div className="mt-2 bg-cyber-panel border border-cyber-border rounded-lg overflow-hidden">
            <div className="px-4 py-2 border-b border-white/5">
              <span className="text-xs font-mono text-white/30 uppercase tracking-wider">Recent Searches</span>
            </div>
            <div className="divide-y divide-white/5">
              {search.history.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => handleHistorySelect(item)}
                  className="w-full p-3 flex items-center gap-3 hover:bg-white/5 transition-colors text-left group"
                >
                  {/* Clock icon */}
                  <div className="text-white/20">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  
                  {/* Avatar */}
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center font-display text-sm font-bold"
                    style={{ 
                      backgroundColor: `${SUBMOLT_COLORS[item.main_submolt] || SUBMOLT_COLORS.default}30`,
                      color: SUBMOLT_COLORS[item.main_submolt] || SUBMOLT_COLORS.default,
                    }}
                  >
                    {item.name.charAt(0).toUpperCase()}
                  </div>
                  
                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-white/80 group-hover:text-cyber-glow transition-colors">
                      @{item.name}
                    </span>
                  </div>
                  
                  {/* Karma */}
                  <div className="font-mono text-xs text-white/40">
                    ⚡ {item.karma?.toLocaleString()}
                  </div>
                  
                  {/* Enter hint for first item */}
                  {index === 0 && (
                    <div className="text-xs text-white/20">
                      <kbd className="px-1.5 py-0.5 bg-white/10 rounded">↵</kbd>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Agent Results */}
        {search.query.length >= 2 && search.mode === 'agents' && (
          <div className="mt-2 bg-cyber-panel border border-cyber-border rounded-lg overflow-hidden max-h-80 overflow-y-auto">
            {search.results.length === 0 ? (
              <div className="p-8 text-center text-white/40 font-mono">
                <p>No agents found matching "{search.query}"</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {search.results.map((agent, index) => (
                  <button
                    key={agent.id}
                    onClick={() => handleSelect(agent)}
                    className="w-full p-4 flex items-center gap-4 hover:bg-white/5 transition-colors text-left group"
                  >
                    {/* Avatar */}
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center font-display text-lg font-bold"
                      style={{ 
                        backgroundColor: `${SUBMOLT_COLORS[agent.main_submolt]}30`,
                        color: SUBMOLT_COLORS[agent.main_submolt],
                      }}
                    >
                      {agent.name.charAt(0).toUpperCase()}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-white group-hover:text-cyber-glow transition-colors">
                          @{agent.name}
                        </span>
                        <span 
                          className="px-2 py-0.5 rounded text-xs font-mono"
                          style={{ 
                            backgroundColor: `${SUBMOLT_COLORS[agent.main_submolt]}20`,
                            color: SUBMOLT_COLORS[agent.main_submolt],
                          }}
                        >
                          m/{agent.main_submolt}
                        </span>
                      </div>
                      <p className="text-sm text-white/40 truncate mt-1">
                        {agent.description || 'No manifesto'}
                      </p>
                    </div>
                    
                    {/* Karma */}
                    <div className="text-right">
                      <div className="font-mono text-cyber-glow">
                        ⚡ {agent.karma?.toLocaleString()}
                      </div>
                      <div className="text-xs text-white/30 font-mono">
                        {agent.post_count || 0} posts
                      </div>
                    </div>
                    
                    {/* Index hint */}
                    {index === 0 && (
                      <div className="text-xs text-white/20">
                        <kbd className="px-1.5 py-0.5 bg-white/10 rounded">↵</kbd>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Post Results */}
        {search.query.length >= 2 && search.mode === 'posts' && (
          <div className="mt-2 bg-cyber-panel border border-cyber-border rounded-lg overflow-hidden max-h-80 overflow-y-auto">
            {search.isSearchingPosts ? (
              <div className="p-8 text-center text-white/40 font-mono">
                <div className="animate-pulse">Searching posts...</div>
              </div>
            ) : search.postResults.length === 0 ? (
              <div className="p-8 text-center text-white/40 font-mono">
                <p>No posts found matching "{search.query}"</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {search.postResults.map((post, index) => {
                  const submoltColor = SUBMOLT_COLORS[post.submolt] || SUBMOLT_COLORS[post.author_submolt] || SUBMOLT_COLORS.default;
                  return (
                    <button
                      key={post.id}
                      onClick={() => handlePostSelect(post)}
                      className="w-full p-4 hover:bg-white/5 transition-colors text-left group"
                    >
                      {/* Author info */}
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center font-display text-xs font-bold"
                          style={{ 
                            backgroundColor: `${submoltColor}30`,
                            color: submoltColor,
                          }}
                        >
                          {post.author_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <span className="font-mono text-sm text-white/70 group-hover:text-cyber-glow transition-colors">
                          @{post.author_name || 'unknown'}
                        </span>
                        <span 
                          className="px-1.5 py-0.5 rounded text-[10px] font-mono"
                          style={{ 
                            backgroundColor: `${submoltColor}20`,
                            color: submoltColor,
                          }}
                        >
                          m/{post.submolt}
                        </span>
                        <span className="text-xs text-white/30 ml-auto">
                          {post.created_at ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) : ''}
                        </span>
                        {index === 0 && (
                          <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-xs text-white/20">↵</kbd>
                        )}
                      </div>
                      
                      {/* Post title */}
                      {post.title && (
                        <h4 className="font-mono text-sm text-white/90 mb-1 line-clamp-1">
                          {post.title}
                        </h4>
                      )}
                      
                      {/* Post content preview */}
                      <p className="text-sm text-white/50 line-clamp-2">
                        {post.content}
                      </p>
                      
                      {/* Votes */}
                      <div className="flex items-center gap-3 mt-2 text-xs text-white/40">
                        <span className="text-green-400/70">▲ {post.upvotes || 0}</span>
                        <span className="text-red-400/70">▼ {post.downvotes || 0}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Hints */}
        <div className="mt-4 flex justify-center gap-4 sm:gap-6 text-[10px] sm:text-xs text-white/30 font-mono flex-wrap">
          <span className="hidden sm:inline">⌘K to search</span>
          <span>Tab to switch</span>
          <span>↵ to select</span>
          <span>ESC to close</span>
        </div>
      </div>
    </div>
  );
}
